import * as React from 'react'
import { theme } from '../theme'
import { ImageTransform } from './types'
import { scaleIncrement, minScale, maxScale } from './constants'

interface Props {
	transform: ImageTransform
	onTransformChange: (transform: ImageTransform) => void
	stepSize?: number
}

export const TransformControls = ({
	transform,
	onTransformChange,
	stepSize = 10
}: Props) => {
	// Handle position changes
	const moveUp = () => {
		onTransformChange({
			...transform,
			position: {
				...transform.position,
				y: transform.position.y - stepSize
			}
		})
	}

	const moveDown = () => {
		onTransformChange({
			...transform,
			position: {
				...transform.position,
				y: transform.position.y + stepSize
			}
		})
	}

	const moveLeft = () => {
		onTransformChange({
			...transform,
			position: {
				...transform.position,
				x: transform.position.x - stepSize
			}
		})
	}

	const moveRight = () => {
		onTransformChange({
			...transform,
			position: {
				...transform.position,
				x: transform.position.x + stepSize
			}
		})
	}

	// Handle scale changes
	const zoomIn = () => {
		const newScale = Math.min(maxScale, transform.scale + scaleIncrement)
		onTransformChange({
			...transform,
			scale: newScale
		})
	}

	const zoomOut = () => {
		const newScale = Math.max(minScale, transform.scale - scaleIncrement)
		onTransformChange({
			...transform,
			scale: newScale
		})
	}

	// Common button styles
	const buttonStyle: React.CSSProperties = {
		width: '32px',
		height: '32px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: theme.colors.background,
		borderWidth: '1px',
		borderStyle: 'solid',
		borderColor: theme.colors.border,
		borderRadius: theme.borderRadius.sm,
		cursor: 'pointer',
		color: theme.colors.text.primary,
		fontSize: '16px',
		transition: 'background-color 0.2s, color 0.2s, border-color 0.2s',
	}

	const buttonHoverStyle: React.CSSProperties = {
		backgroundColor: theme.colors.primary + '10',
		borderColor: theme.colors.primary,
		color: theme.colors.primary,
	}

	// State for hover effects
	const [hoveredButton, setHoveredButton] = React.useState<string | null>(null)

	return (
		<div style={{ marginBottom: theme.spacing.md }}>
			<div style={{
				display: 'flex',
				flexDirection: 'column',
				gap: theme.spacing.sm,
				marginBottom: theme.spacing.sm
			}}>
				<div style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					fontSize: theme.typography.sizes.sm,
					color: theme.colors.text.primary,
					fontWeight: 500,
				}}>
					<span>Position Controls</span>
				</div>

				{/* Position Controls */}
				<div style={{
					display: 'grid',
					gridTemplateColumns: 'repeat(3, 1fr)',
					gap: theme.spacing.xs,
					width: 'fit-content',
					margin: '0 auto'
				}}>
					{/* Top row */}
					<div style={{ gridColumn: '2', gridRow: '1' }}>
						<button
							onClick={moveUp}
							onMouseEnter={() => setHoveredButton('up')}
							onMouseLeave={() => setHoveredButton(null)}
							style={{
								...buttonStyle,
								...(hoveredButton === 'up' ? buttonHoverStyle : {})
							}}
							title="Move Up"
						>
							↑
						</button>
					</div>

					{/* Middle row */}
					<div style={{ gridColumn: '1', gridRow: '2' }}>
						<button
							onClick={moveLeft}
							onMouseEnter={() => setHoveredButton('left')}
							onMouseLeave={() => setHoveredButton(null)}
							style={{
								...buttonStyle,
								...(hoveredButton === 'left' ? buttonHoverStyle : {})
							}}
							title="Move Left"
						>
							←
						</button>
					</div>
					<div style={{ gridColumn: '3', gridRow: '2' }}>
						<button
							onClick={moveRight}
							onMouseEnter={() => setHoveredButton('right')}
							onMouseLeave={() => setHoveredButton(null)}
							style={{
								...buttonStyle,
								...(hoveredButton === 'right' ? buttonHoverStyle : {})
							}}
							title="Move Right"
						>
							→
						</button>
					</div>

					{/* Bottom row */}
					<div style={{ gridColumn: '2', gridRow: '3' }}>
						<button
							onClick={moveDown}
							onMouseEnter={() => setHoveredButton('down')}
							onMouseLeave={() => setHoveredButton(null)}
							style={{
								...buttonStyle,
								...(hoveredButton === 'down' ? buttonHoverStyle : {})
							}}
							title="Move Down"
						>
							↓
						</button>
					</div>
				</div>
			</div>

			{/* Zoom Controls */}
			<div style={{
				display: 'flex',
				flexDirection: 'column',
				gap: theme.spacing.sm
			}}>
				<div style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					fontSize: theme.typography.sizes.sm,
					color: theme.colors.text.primary,
					fontWeight: 500,
				}}>
					<span>Zoom Controls</span>
					<span style={{
						fontSize: theme.typography.sizes.tiny,
						color: theme.colors.text.secondary
					}}>
						{(transform.scale * 100).toFixed(0)}%
					</span>
				</div>

				<div style={{
					display: 'flex',
					justifyContent: 'space-between',
					gap: theme.spacing.md
				}}>
					<button
						onClick={zoomOut}
						onMouseEnter={() => setHoveredButton('zoomOut')}
						onMouseLeave={() => setHoveredButton(null)}
						style={{
							...buttonStyle,
							flex: 1,
							...(hoveredButton === 'zoomOut' ? buttonHoverStyle : {})
						}}
						title="Zoom Out"
						disabled={transform.scale <= minScale}
					>
						−
					</button>
					<button
						onClick={zoomIn}
						onMouseEnter={() => setHoveredButton('zoomIn')}
						onMouseLeave={() => setHoveredButton(null)}
						style={{
							...buttonStyle,
							flex: 1,
							...(hoveredButton === 'zoomIn' ? buttonHoverStyle : {}),
							opacity: transform.scale >= maxScale ? 0.5 : 1
						}}
						title="Zoom In"
						disabled={transform.scale >= maxScale}
					>
						+
					</button>
				</div>
			</div>
		</div>
	)
}
